import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { PLANETARY_PILLARS } from '../../data/ecoContent';
import { DoubleBezelCard } from '../common/DoubleBezelCard';
import { PixelGlyph } from '../common/PixelGlyph';

gsap.registerPlugin(ScrollTrigger);

export const PlanetaryPillars: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const track = trackRef.current;
    if (!section || !track) return;

    const matchMedia = window.matchMedia('(min-width: 1024px)');
    if (!matchMedia.matches) return;

    const totalWidth = track.scrollWidth - window.innerWidth + 120;

    const ctx = gsap.context(() => {
      gsap.to(track, {
        x: -totalWidth,
        ease: 'none',
        scrollTrigger: {
          trigger: section,
          pin: true,
          scrub: 1.2,
          start: 'top top',
          end: () => `+=${totalWidth * 1.3}`,
          invalidateOnRefresh: true,
        },
      });
    }, section);

    return () => ctx.revert();
  }, []);

  return (
    <section
      id="pillars"
      ref={sectionRef}
      className="relative min-h-screen py-20 sm:py-28 lg:py-0 overflow-hidden bg-alabaster-100 z-10 flex flex-col justify-center border-t border-carbon-900/10"
      aria-label="Planetary Biome Atlas"
    >
      {/* Section Header */}
      <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 mb-8 sm:mb-12 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs font-mono text-chlorophyll-deep tracking-widest uppercase mb-2">
            <span className="w-2 h-2 bg-chlorophyll inline-block" />
            <span>03 // PLANETARY RESTORATION CODEX</span>
          </div>
          <h2 className="font-serif font-black text-4xl sm:text-5xl md:text-6xl text-carbon-900 tracking-tight">
            Four continental pillars.
          </h2>
        </div>
        <div className="hidden lg:flex text-xs font-mono text-carbon-900/60 items-center gap-2">
          <span>TRAVERSE ARCHIVAL ATLAS HORIZONTALLY</span>
          <PixelGlyph name="arrow-right" size={14} color="#059669" />
        </div>
      </div>

      {/* Horizontal Track Container */}
      <div className="w-full overflow-x-auto lg:overflow-visible no-scrollbar">
        <div
          ref={trackRef}
          className="flex flex-col lg:flex-row items-stretch gap-6 sm:gap-8 px-4 sm:px-6 lg:px-24 w-full lg:w-max will-change-transform"
        >
          {PLANETARY_PILLARS.map((pillar) => (
            <div
              key={pillar.id}
              className="w-full lg:w-[660px] shrink-0"
              data-cursor="interactive"
            >
              <DoubleBezelCard
                enableTilt={true}
                glowColor={
                  pillar.accentColor === '#F59E0B'
                    ? 'solar'
                    : pillar.accentColor === '#38BDF8'
                    ? 'ozone'
                    : 'emerald'
                }
                className="p-6 sm:p-8 md:p-10 flex flex-col justify-between h-full min-h-[420px] sm:min-h-[460px]"
              >
                <div>
                  {/* Top Archival Plate ID */}
                  <div className="flex items-center justify-between pb-4 sm:pb-5 mb-5 border-b border-carbon-900/10 text-xs font-mono">
                    <span className="text-carbon-900/60 tracking-wider text-[11px] uppercase">
                      ARCHIVE [{pillar.index}] • {pillar.tag}
                    </span>
                    <span className="font-serif font-bold text-sm text-carbon-900">
                      § 0{pillar.index}
                    </span>
                  </div>

                  <h3 className="font-serif font-bold text-2xl sm:text-3xl text-carbon-900 mb-2.5">
                    {pillar.title}
                  </h3>
                  <p className="text-sm sm:text-base text-carbon-900/80 font-medium mb-4 leading-relaxed">
                    {pillar.subtitle}
                  </p>
                  <p className="text-xs sm:text-sm text-carbon-900/60 leading-relaxed mb-6">
                    {pillar.description}
                  </p>
                </div>

                {/* Technical Metric Row */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-5 border-t border-carbon-900/10 bg-alabaster-200/60 rounded-lg p-4">
                  {pillar.metrics.map((m, mIdx) => (
                    <div key={mIdx} className="px-1 flex items-center justify-between sm:block">
                      <div className="text-[10px] font-mono text-carbon-900/50 tracking-wider uppercase mb-1">
                        {m.label}
                      </div>
                      <div className="text-sm sm:text-base font-mono font-bold text-carbon-900 truncate">
                        {m.value}
                      </div>
                    </div>
                  ))}
                </div>
              </DoubleBezelCard>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
